import React from 'react';
import StatusDropdown from '../../../StatusDropdown';
import PriorityDropdown from '../../../../FlowchartView/TaskNode/PriorityDropdown';
import DifficultyDropdown from '../../../../FlowchartView/TaskNode/DifficultyDropdown';
import CustomFieldDropdown from '../../CustomFieldsDisplay/CustomFieldDropdown';

const CLASS_PREFIX = 'mission-card';

/**
 * DropdownRows Component
 * 
 * Builds rows of dropdown fields (status, priority, difficulty, custom select fields)
 * with a maximum of 2 dropdowns per row.
 * 
 * This component handles the complex logic of:
 * - Collecting enabled dropdowns (built-in + custom)
 * - Sorting custom fields by order
 * - Splitting into rows of 2
 * - Applying correct CSS classes based on dropdown count
 */
const DropdownRows = ({
  task,
  // Display flags
  showStatusDropdown,
  showPriorityDropdown,
  showDifficultyDropdown,
  // Status dropdown props
  isLocked,
  lockReason,
  blockingDependencies,
  nestedSprint,
  onStatusChange,
  onSetStatusAutoSync,
  statuses,
  allTasks,
  onAddStatus,
  onDeleteStatus,
  onRenameStatus,
  onGoToDependency,
  parentMissionName,
  parentCampaignName,
  onGoToParentMission,
  onSubmitDeliverable,
  onOpenDetails,
  // Priority dropdown props
  onPriorityChange,
  // Difficulty dropdown props
  onDifficultyChange,
  // Custom fields
  customFields,
  cardDisplaySettings,
  onCustomFieldValueChange,
}) => {
  // Collect enabled dropdowns (built-in + custom select fields)
  const dropdowns = [];
  
  // Built-in dropdowns
  if (showStatusDropdown) {
    dropdowns.push(
      <div key="status" className={`${CLASS_PREFIX}__status-row`}>
          <StatusDropdown
            variant="button"
            taskId={task.id}
            taskName={task.title || task.name || 'this mission'}
            currentStatus={task.status}
            isLocked={isLocked}
            lockReason={lockReason}
            blockingDependencies={blockingDependencies}
            parentMissionName={parentMissionName}
            parentCampaignName={parentCampaignName}
            onGoToParentMission={onGoToParentMission}
            hasNestedSprint={!!nestedSprint}
            nestedSprintProgress={nestedSprint?.progress}
            statusAutoSync={task.statusAutoSync}
            onStatusChange={onStatusChange}
            onSetStatusAutoSync={onSetStatusAutoSync}
            statuses={statuses}
            allTasks={allTasks}
            onAddStatus={onAddStatus}
            onDeleteStatus={onDeleteStatus}
            onRenameStatus={onRenameStatus}
            deliverables={task.deliverables || []}
            onSubmitDeliverable={onSubmitDeliverable}
            onOpenDetails={onOpenDetails}
            showLabel={true}
            onGoToDependency={onGoToDependency}
          />
      </div>
    );
  }
  
  if (showPriorityDropdown) {
    dropdowns.push(
      <div key="priority" className={`${CLASS_PREFIX}__priority-row`}>
        <PriorityDropdown
          taskId={task.id}
          currentPriority={task.priority}
          onPriorityChange={onPriorityChange}
        />
      </div>
    );
  }
  
  if (showDifficultyDropdown) {
    dropdowns.push(
      <div key="difficulty" className={`${CLASS_PREFIX}__difficulty-row`}>
        <DifficultyDropdown
          taskId={task.id}
          currentDifficulty={task.difficulty}
          onDifficultyChange={onDifficultyChange}
        />
      </div>
    );
  }
  
  // Custom select field dropdowns (type === 'select' and visible)
  const customSelectFields = customFields
    .filter(f => f.type === 'select' && cardDisplaySettings[f.id] === true)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  
  customSelectFields.forEach(field => {
    const fieldValues = task?.customFieldValues || {};
    dropdowns.push(
      <div key={`custom-${field.id}`} className={`${CLASS_PREFIX}__custom-dropdown-row`}>
        <CustomFieldDropdown
          field={field}
          value={fieldValues[field.id]}
          onChange={onCustomFieldValueChange}
          taskId={task.id}
        />
      </div>
    );
  });
  
  // Don't render anything if no dropdowns
  if (dropdowns.length === 0) return null;
  
  // Split into rows of 2
  const rows = [];
  for (let i = 0; i < dropdowns.length; i += 2) {
    const rowDropdowns = dropdowns.slice(i, i + 2);
    const rowClass = rowDropdowns.length === 2 
      ? `${CLASS_PREFIX}__status-priority-row ${CLASS_PREFIX}__status-priority-row--both`
      : `${CLASS_PREFIX}__status-priority-row`;
    rows.push(
      <div key={`row-${i}`} className={rowClass}>
        {rowDropdowns}
      </div>
    );
  }
  
  return rows;
};

export default DropdownRows;
